/**
 * Editing the words on the screen they appear on.
 *
 * WHY THERE IS A COMPONENT AS WELL AS A FUNCTION. `t()` returns a string, and
 * a string has no position in the document — nothing to outline and nothing to
 * click. So a string that should be editable in place is written as `<T>`,
 * which renders the same text and, only while edit mode is on, wraps it in a
 * span carrying its key.
 *
 *   <th><T k="sponsors.col.status" d="Status" /></th>
 *   placeholder={t('sponsors.search.placeholder', 'Search sponsors…')}
 *
 * WHICH ONE TO USE. `<T>` wherever the string sits in element content that can
 * hold markup. `t()` in the two places a span cannot go:
 *
 *   - an ATTRIBUTE — placeholder, aria-label, title — which takes a string
 *   - inside <option>, where a span is invalid HTML. Browsers flatten it to
 *     text, so it renders correctly and can never be clicked, which is worse
 *     than not marking it: it looks editable in the source and is not.
 *
 * Both forms are found by the extractor, so either way the string reaches the
 * catalogue and the reviewer's document. The difference is only whether it can
 * be edited by clicking it or has to be edited from the catalogue screen —
 * which is one of the reasons that screen exists rather than being a
 * convenience.
 *
 * NOTHING IS ADDED WHEN EDIT MODE IS OFF, which is the normal state for every
 * user on every page load. `<T>` renders a bare fragment, so the document is
 * byte-identical to what it would be with a plain string, and no client
 * administrator ever pays for a feature they cannot use.
 *
 * THE CATALOGUE IS FETCHED LAZILY, the first time somebody turns editing on.
 * It is only needed to warn about shared strings and required variables, and
 * loading it at boot would put an admin-only request on everybody's first
 * paint.
 */

import {
  Fragment,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { t } from './copy';

/** What the editor knows about one string, from the catalogue. */
interface Meta {
  shipped: string;
  vars: string[];
  /** Those of `vars` that may be removed. */
  optional: string[];
  uses: number;
  where: string[];
  changed: boolean;
}

interface EditContext {
  on: boolean;
  /** Bumped after every save, so every <T> re-reads window.__COPY__. */
  revision: number;
  open: (key: string, fallback: string, el: HTMLElement) => void;
}

const Ctx = createContext<EditContext | null>(null);

/** Where the editor is pointing, and at what. */
interface Target {
  key: string;
  fallback: string;
  rect: DOMRect;
}

export function CopyEditProvider({
  enabled,
  children,
}: {
  /** Front Porch administrators only. False for everyone else, and the toggle
   *  is not drawn at all. The API refuses regardless. */
  enabled: boolean;
  children: ReactNode;
}) {
  const [on, setOn] = useState(false);
  const [revision, setRevision] = useState(0);
  const [target, setTarget] = useState<Target | null>(null);
  const [catalogue, setCatalogue] = useState<Record<string, Meta> | null>(null);

  /* Fetched once, when editing is first switched on. */
  useEffect(() => {
    if (!on || catalogue) return;
    void fetch('/api/content')
      .then((r) => (r.ok ? r.json() : { strings: [] }))
      .then(
        (body: {
          strings: {
            key: string;
            shipped: string;
            vars: string[];
            optional: string[];
            uses: number;
            where: string[];
            changed: boolean;
          }[];
        }) => {
          setCatalogue(
            Object.fromEntries(
              body.strings.map((s) => [
                s.key,
                {
                  shipped: s.shipped,
                  vars: s.vars,
                  optional: s.optional ?? [],
                  uses: s.uses,
                  where: s.where,
                  changed: s.changed,
                },
              ]),
            ),
          );
        },
      )
      .catch(() => setCatalogue({}));
  }, [on, catalogue]);

  const open = useCallback((key: string, fallback: string, el: HTMLElement) => {
    setTarget({ key, fallback, rect: el.getBoundingClientRect() });
  }, []);

  const value = useMemo(() => ({ on, revision, open }), [on, revision, open]);

  if (!enabled) return <>{children}</>;

  return (
    <Ctx.Provider value={value}>
      <div className={on ? 'copy-editing' : undefined}>{children}</div>

      <button
        type="button"
        className={`copy-toggle${on ? ' on' : ''}`}
        aria-pressed={on}
        onClick={() => {
          setOn((v) => !v);
          setTarget(null);
        }}
      >
        <span className="copy-toggle-sw" aria-hidden="true" />
        {on ? 'Editing text' : 'Edit text'}
      </button>

      {target && (
        <Editor
          target={target}
          meta={catalogue?.[target.key]}
          onClose={() => setTarget(null)}
          onSaved={(key, value) => {
            /* Written straight into the injected map so every <T> showing this
               key updates now, rather than on the next full page load. */
            window.__COPY__ = { ...(window.__COPY__ ?? {}), [key]: value };
            setCatalogue((c) => (c ? { ...c, [key]: { ...c[key]!, changed: true } } : c));
            setRevision((r) => r + 1);
            setTarget(null);
          }}
          onReverted={(key, shipped) => {
            const next = { ...(window.__COPY__ ?? {}) };
            delete next[key];
            window.__COPY__ = next;
            setCatalogue((c) => (c ? { ...c, [key]: { ...c[key]!, changed: false, shipped } } : c));
            setRevision((r) => r + 1);
            setTarget(null);
          }}
        />
      )}
    </Ctx.Provider>
  );
}

/**
 * One editable string.
 *
 * Renders exactly the text `t()` would, and becomes clickable only while an
 * administrator has editing switched on.
 */
export function T({
  k,
  d,
  vars,
}: {
  k: string;
  d: string;
  vars?: Record<string, string | number>;
  /**
   * Variables the sentence can do without.
   *
   * READ FROM THE SOURCE, NOT USED AT RUNTIME. The extractor records it so the
   * editor knows which variables it may let somebody delete. A tier is the
   * example: plenty of sponsors have none, so the sentence already has to read
   * correctly without it, and refusing to save an edit that drops it would be
   * protecting something that is empty half the time anyway. A count is the
   * opposite — lose {n} and the number has nowhere else to go.
   *
   * This mirrors `available` and `required` on EMAIL_TEMPLATES, which has
   * drawn the same distinction since the email editor was built.
   */
  optional?: string[];
}) {
  const ctx = useContext(Ctx);
  /* `revision` is read so a save re-renders every instance of the key. */
  const text = useMemo(() => t(k, d, vars), [k, d, vars, ctx?.revision]);

  if (!ctx?.on) return <>{withBreaks(text)}</>;

  return (
    <span
      className="copy-mark"
      data-copy-key={k}
      role="button"
      tabIndex={0}
      title={`Edit: ${k}`}
      onClick={(e) => {
        /* Stopped so editing a label on a row does not also follow the row's
           link or fire its button. */
        e.preventDefault();
        e.stopPropagation();
        ctx.open(k, d, e.currentTarget);
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        e.stopPropagation();
        ctx.open(k, d, e.currentTarget);
      }}
    >
      {withBreaks(text)}
    </span>
  );
}

/**
 * Renders the line breaks somebody typed.
 *
 * HTML COLLAPSES NEWLINES TO A SPACE, which is not what anybody means when
 * they press Enter in a text box. The first edit made through this editor put
 * a blank line in the portal greeting, saved correctly, shipped correctly, and
 * rendered as one run-on sentence — so the feature looked broken while working
 * exactly as written.
 *
 * Only touched when a newline is actually present, so every single-line string
 * in the application renders exactly the node it did before. A blank line
 * becomes two breaks, which reads as a paragraph without needing the editor to
 * produce real paragraph markup.
 */
function withBreaks(text: string): ReactNode {
  if (!text.includes('\n')) return text;

  return text.split('\n').map((line, i) =>
    i === 0 ? (
      line
    ) : (
      <Fragment key={i}>
        <br />
        {line}
      </Fragment>
    ),
  );
}

/**
 * The editor itself.
 *
 * Anchored under whatever was clicked, clamped to the window. Fixed position
 * rather than absolute, because the thing it points at may be inside a
 * scrolling table.
 */
function Editor({
  target,
  meta,
  onClose,
  onSaved,
  onReverted,
}: {
  target: Target;
  meta: Meta | undefined;
  onClose: () => void;
  onSaved: (key: string, value: string) => void;
  onReverted: (key: string, shipped: string) => void;
}) {
  const shipped = meta?.shipped ?? target.fallback;
  const [draft, setDraft] = useState(() => t(target.key, target.fallback));
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const box = useRef<HTMLTextAreaElement>(null);

  /** Puts a variable at the caret, replacing any selection. */
  function insertVar(token: string) {
    const el = box.current;
    if (!el) {
      setDraft((d) => d + token);
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? start;
    setDraft(el.value.slice(0, start) + token + el.value.slice(end));
    window.setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    }, 0);
  }

  async function save() {
    setSaving(true);
    setProblem(null);
    try {
      const res = await fetch(`/api/content/${encodeURIComponent(target.key)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: draft }),
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string; value?: string; changed?: boolean };

      if (!res.ok) {
        setProblem(body.error ?? 'That did not save.');
        return;
      }
      /* The server decides what the value ended up being: clearing it, or
         typing the shipped wording back, both mean "no override". */
      if (body.changed) onSaved(target.key, body.value ?? draft);
      else onReverted(target.key, body.value ?? shipped);
    } catch {
      setProblem('That did not save. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  }

  async function revert() {
    setSaving(true);
    try {
      await fetch(`/api/content/${encodeURIComponent(target.key)}`, { method: 'DELETE' });
      onReverted(target.key, shipped);
    } finally {
      setSaving(false);
    }
  }

  /* Clamped so the panel is never half off the screen, and flipped above the
     target when there is no room below it. */
  const width = 370;
  const left = Math.min(Math.max(12, target.rect.left), window.innerWidth - width - 12);
  const below = target.rect.bottom + 8;
  const flip = below + 260 > window.innerHeight;
  const top = flip ? Math.max(12, target.rect.top - 268) : below;

  return (
    <>
      {/* Catches the click that closes the panel, without a document-level
          listener that would also have to know about the panel itself. */}
      <div className="copy-scrim" onClick={onClose} />

      <div className="copy-pop" style={{ left, top, width }} role="dialog" aria-label="Edit this text">
        <p className="copy-pop-key">{target.key}</p>

        <textarea
          ref={box}
          className="fi ft"
          rows={2}
          autoFocus
          value={draft}
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void save();
            }
          }}
        />

        <p className="copy-pop-shipped">
          Shipped: <b>{shipped}</b>
        </p>
        <p className="copy-pop-shipped">A line break here shows on the page.</p>

        {/* COUNTED IN SCREENS, not call sites. A string used twice within one
            component is still one screen, and "changes in 2 places: Library
            detail" reads as though the same page changes twice. What an
            administrator needs is the list of screens to go and look at. */}
        {meta && meta.where.length > 1 && (
          <p className="copy-pop-flag">
            Also on {meta.where.length - 1} other{' '}
            {meta.where.length === 2 ? 'screen' : 'screens'}, and they all change together:{' '}
            {meta.where.join(', ')}.
          </p>
        )}
        {/* OFFERED, not just policed. Naming the variables only to forbid
            removing them tells somebody what they cannot do and not what they
            can; the email template editor has offered its fields for insertion
            since it was built. */}
        {meta && meta.vars.length > 0 && (
          <div className="copy-pop-vars">
            <span className="copy-pop-vars-label">Insert</span>
            {meta.vars.map((v) => {
              const required = !meta.optional.includes(v);
              return (
                <button
                  key={v}
                  type="button"
                  className={`copy-var${required ? ' is-required' : ''}`}
                  title={
                    required
                      ? 'Cannot be removed: its value has nowhere else to appear'
                      : 'Optional. The sentence reads correctly without it'
                  }
                  onClick={() => insertVar(v)}
                >
                  {v}
                </button>
              );
            })}
          </div>
        )}
        {problem && <p className="auth-error">{problem}</p>}

        <div className="copy-pop-actions">
          <button type="button" className="btn-compact" disabled={saving} onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="btn-compact btn-compact--quiet" disabled={saving} onClick={onClose}>
            Cancel
          </button>
          {meta?.changed && (
            <button type="button" className="copy-pop-reset" disabled={saving} onClick={() => void revert()}>
              Reset to what shipped
            </button>
          )}
        </div>
      </div>
    </>
  );
}
